import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Loader2, X } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { useTelegram } from '@/hooks/useTelegram';
import { useUserStore } from '@/store/userStore';
import { supabase } from '@/lib/supabase';
import { analyzePhoto } from '@/lib/ai';
import type { AIAnalysisResult } from '@/types';
import demoPlate from '@/assets/demo/demo-plate.jpg';

// ════════════════════════════════════════════════════════════════
// «Попробуй на примере» (решение Славы 22.09.2026).
//
// Разбор 22.09: новичок приходит вечером по ссылке из поста, еды под рукой нет,
// видит пустое кольцо и уходит, так и не увидев главную фишку — распознавание
// по фото. Здесь то же распознавание вживую (настоящий analyze-meal), но на
// готовой тарелке. В дневник НИЧЕГО не пишется.
//
// Показывается, пока у человека нет ни одной записи еды. Фото — собственный
// снимок Славы (владелец данных), не пользовательский. Один прогон ≈ 0,46 ₽,
// результат кешируется на сессию.
// ════════════════════════════════════════════════════════════════

const CACHE_KEY = 'demo_photo_result';
const HIDE_KEY = 'demo_photo_hidden';

async function assetToBase64(url: string): Promise<string> {
  const blob = await (await fetch(url)).blob();
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

export function DemoPhotoCard() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { profile } = useUserStore();
  const [hasMeals, setHasMeals] = useState<boolean | null>(null);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(HIDE_KEY) === '1'; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [result, setResult] = useState<AIAnalysisResult | null>(() => {
    try { const c = sessionStorage.getItem(CACHE_KEY); return c ? JSON.parse(c) : null; } catch { return null; }
  });

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('meals')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', profile.id)
      .then(({ count }) => setHasMeals((count ?? 0) > 0));
  }, [profile?.id]);

  if (hasMeals !== false || hidden) return null;

  async function run() {
    if (busy) return;
    haptic('light');
    setBusy(true);
    setError(false);
    try {
      const r = await analyzePhoto(await assetToBase64(demoPlate));
      setResult(r);
      haptic('success');
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(r)); } catch {}
    } catch (e) {
      console.warn('[DemoPhotoCard]', e);
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  function hide() {
    setHidden(true);
    try { sessionStorage.setItem(HIDE_KEY, '1'); } catch {}
  }

  const total = result?.items.reduce(
    (a, it) => ({ kcal: a.kcal + it.calories, p: a.p + it.protein_g, f: a.f + it.fat_g, c: a.c + it.carbs_g }),
    { kcal: 0, p: 0, f: 0, c: 0 },
  );

  return (
    <div className="card p-4 mb-4 relative">
      <button type="button" onClick={hide} className="absolute top-3 right-3 p-1 text-ink-dim" aria-label="Скрыть пример">
        <X size={16} />
      </button>
      <div className="flex items-center gap-3 mb-3 pr-6">
        <Mascot pose={busy ? 'think' : result ? 'celebrate' : 'wave'} compact float={false} className="w-10 h-10 shrink-0" />
        <div>
          <div className="text-sm font-medium">{result ? 'Вот что Дофи увидел' : 'Посмотри, как это работает'}</div>
          <div className="text-xs text-ink-muted">
            {result ? 'Это пример, в дневник он не записан' : 'Еды под рукой нет? Дофи разберёт тарелку-пример'}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="relative w-28 h-28 shrink-0 rounded-xl overflow-hidden">
          <img src={demoPlate} alt="Тарелка-пример: овсяноблин с ветчиной, овощи, фрукты и кофе" className="w-full h-full object-cover" />
          {busy && (
            <motion.div
              className="absolute inset-x-0 h-8 bg-gradient-to-b from-transparent via-emerald-glow/40 to-transparent"
              initial={{ top: '-30%' }}
              animate={{ top: '100%' }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {result && total ? (
              <motion.div key="res" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1">
                {result.items.slice(0, 4).map((it, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.12 }}
                    className="flex justify-between gap-2 text-xs"
                  >
                    <span className="truncate">{it.name}</span>
                    <span className="text-ink-dim shrink-0 tabular-nums">~{Math.round(it.calories)} ккал</span>
                  </motion.div>
                ))}
                <div className="pt-1.5 mt-1.5 border-t border-line/60 text-xs tabular-nums">
                  <b className="text-emerald-glow">~{Math.round(total.kcal)} ккал</b>
                  <span className="text-ink-dim"> · Б {Math.round(total.p)} · Ж {Math.round(total.f)} · У {Math.round(total.c)}</span>
                </div>
              </motion.div>
            ) : (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col justify-center">
                <p className="text-xs text-ink-muted leading-relaxed mb-2.5">
                  {busy ? 'Дофи смотрит на тарелку…' : error ? 'Не получилось с первого раза. Попробуем ещё?' : 'Калории и БЖУ по одному фото — за несколько секунд.'}
                </p>
                <button
                  type="button"
                  onClick={run}
                  disabled={busy}
                  className="self-start px-3 py-2 rounded-xl bg-emerald-glow/15 border border-emerald-glow/40 text-emerald-glow text-xs font-bold disabled:opacity-60 flex items-center gap-1.5"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                  {busy ? 'Распознаю…' : 'Распознать пример'}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {result && (
        <button
          type="button"
          onClick={() => { haptic('medium'); nav('/add?mode=photo'); }}
          className="w-full mt-3 py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
        >
          <Camera size={16} /> Теперь сфоткай свою еду
        </button>
      )}
    </div>
  );
}
